import * as ethers from 'ethers';
import { useAccount } from 'wagmi';
import { useEffect, useState } from 'react';
import { useResponsive } from '@op-bridge/bridge-core';
import { Address, parseAbi, parseUnits } from 'viem';

import * as env from '../env';
import { batchUpdate, getEtherFormatValue } from '../utils';
import { usePending } from './usePending';
import { useTxFailed } from './useTxFailed';
import { useL1Withdraw } from './useL1Withdraw';
import { getPublicClientL1, getWalletClientL1 } from '@/utils/clients';
import { standardBridgeAbi } from '@/base/standardBridgeAbi';
const l1RPC = env.L1_RPC_URL;

const l1RpcProvider = new ethers.providers.JsonRpcProvider(l1RPC);
// const l2RpcProvider = new ethers.providers.JsonRpcProvider(l2RPC);

export const useDeposit = () => {
  const [isSufficient, setIsSufficient] = useState(false);
  const [txFailedMsg, setTxFailedMsg] = useState('');
  const [waitingConfirmation, setWaitingConfirmation] = useState(false);
  const [address, setAddress] = useState<null | string>(null);
  const { isMobile } = useResponsive();

  const { showPending, handlePendingShow, handlePendingToggle } = usePending();
  const { showError, handleErrorShow, handleErrorToggle } = useTxFailed();

  const { address: wagmiAddress, connector } = useAccount();
  const { crossChainMessenger } = useL1Withdraw();

  useEffect(() => {
    let mount = true;
    if (wagmiAddress && mount) {
      setAddress(wagmiAddress);
    }
    return () => {
      mount = false;
    };
  }, [wagmiAddress]);

  // check account balance
  useEffect(() => {
    let mount = true;
    if (address) {
      reportBalances().then((balance) => {
        if (balance && mount) {
          setIsSufficient(true);
        }
      });
    }
    return () => {
      setIsSufficient(false);
      mount = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address]);

  const reportBalances = async () => {
    if (address) {
      try {
        const balanceObj = await l1RpcProvider.getBalance(address);
        const balance = await getEtherFormatValue(balanceObj);
        if (balance) {
          return balance;
        } else {
          return null;
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.log(e);
        return null;
      }
    }
  };

  const deposit = async (depositAmount: string, gasLimit: number = 100000) => {
    if (!address) return;
    const balance = await reportBalances();
    if (!balance) {
      setIsSufficient(false);
      return;
    }

    if (crossChainMessenger && depositAmount) {
      try {
        // show pending modal
        handlePendingShow(true);
        setWaitingConfirmation(true);

        // start deposit
        // const response = await crossChainMessenger.depositETH(
        //   ethers.utils.parseUnits(depositAmount, 'ether'),
        //   { overrides: { gasLimit: gasLimit }, l2GasLimit: 1 },
        // );

        const provider = await connector?.getProvider();
        const publicClientL1 = getPublicClientL1({ provider });
        const walletClientL1 = getWalletClientL1({
          account: address as `0x${string}`,
          provider,
        });

        const { request } = await publicClientL1.simulateContract({
          address: env.L1StandardBridge as Address,
          abi: standardBridgeAbi,
          functionName: 'depositETHTo',
          args: [address as Address, gasLimit, '0x'],
          value: parseUnits(depositAmount, 18),
          account: address as Address,
        });

        const depositHash = await walletClientL1.writeContract(request);

        const receipt = await publicClientL1.waitForTransactionReceipt({ hash: depositHash });
        // eslint-disable-next-line no-console
        console.dir({ receipt }, { depth: null });
        // end deposit

        setWaitingConfirmation(false);
        handlePendingShow(false);

        return depositHash;
      } catch (e: any) {
        setWaitingConfirmation(false);
        handlePendingShow(false);

        if (e && e?.code) {
          batchUpdate(() => {
            try {
              // do not show error if user accelerate tx (tx hash replaced)
              if (e?.code === 'TRANSACTION_REPLACED' && e?.replacement?.hash) {
                // eslint-disable-next-line no-console
                console.log(e.replacement);
                return e.replacement.hash;
              } else {
                const errorMsg = isMobile ? e?.message || e?.code : e?.code;
                setTxFailedMsg(errorMsg);
              }
              // eslint-disable-next-line no-console
              console.log(e?.code);
            } catch (e) {}
            handleErrorShow(true);
          });
        } else {
          setTxFailedMsg(e || 'Transaction Failed');
        }
        // eslint-disable-next-line no-console
        console.log('deposit error', e);
        return null;
      }
    }
    return null;
  };

  return {
    reportBalances,
    deposit,
    isSufficient,
    waitingConfirmation,
    showPending,
    handlePendingShow,
    handlePendingToggle,
    txFailedMsg,
    showError,
    handleErrorShow,
    handleErrorToggle,
  };
};
